/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withAssign } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { NumericFormat } from '@blade/std-lib'
import { NumberUtil } from '@root/shared'
import { reatomNumericInput } from '@root/shared/lib'

import type { Atom } from '@reatom/framework'
import type { WalletAssetModel } from '@root/entities/wallet'

type Source = 'percentage' | 'amount'

export const reatomDistributeAllocatedAmount = ({
  assetAtom,
}: {
  assetAtom: Atom<WalletAssetModel | undefined>
}) => {
  const sourceAtom = atom<Source>('amount')
  const amountAtom = reatomNumericInput({})
  const percentageAtom = reatomNumericInput({})

  const resolvedAmountAtom = atom((ctx) => {
    const source = ctx.spy(sourceAtom)
    const asset = ctx.spy(assetAtom)
    if (!asset) {
      return BigNumber(0)
    }

    const assetBalance = ctx.spy(asset.balance)

    if (source === 'percentage') {
      const percents = ctx.spy(percentageAtom).dividedBy(100)
      return assetBalance.multipliedBy(percents)
    }

    return ctx.spy(amountAtom)
  })

  const setAmountRawAction = action((ctx, raw: string) => {
    sourceAtom(ctx, 'amount')

    const amountBn = NumberUtil.parseBigNumber(raw)
    const asset = ctx.get(assetAtom)
    if (amountBn && asset) {
      const percentage = amountBn
        .dividedBy(ctx.get(asset.balance))
        .multipliedBy(100)

      percentageAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(percentage, 'percents'),
      )
    }

    amountAtom.setRaw(ctx, raw)
  })

  const setPercentageRawAction = action((ctx, raw: string) => {
    sourceAtom(ctx, 'percentage')
    const bn = NumberUtil.parseBigNumber(raw)
    const asset = ctx.get(assetAtom)

    if (bn && asset) {
      const balance = ctx.get(asset.balance)
      const amount = bn.dividedBy(100).multipliedBy(balance)
      amountAtom.setRaw(ctx, NumericFormat.formatPreset(amount, 'full'))
    }
    percentageAtom.setRaw(ctx, raw)
  })

  return resolvedAmountAtom.pipe(
    withAssign(() => ({
      amount: amountAtom,
      setAmountRaw: setAmountRawAction,
      percentage: percentageAtom,
      setPercentageRaw: setPercentageRawAction,
    })),
  )
}

export type DistributeFundsModel = ReturnType<
  typeof reatomDistributeAllocatedAmount
>
